import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import './AdminNav.scss';

interface NavItem {
    path: string;
    labelKey: string;
    defaultLabel: string;
    icon: React.ReactNode;
}

interface NavGroup {
    key: string;
    labelKey: string;
    defaultLabel: string;
    items: NavItem[];
}

interface AdminNavProps {
    currentPath: string;
    onLinkClick?: () => void;
    /** When true the parent sidebar is collapsed to the icon rail: group labels
     *  hide and every group's items show as icons regardless of open state. */
    collapsed?: boolean;
}

const STORAGE_KEY = 'adminNavState';

const I = ({ children }: { children: React.ReactNode }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

const Icons = {
    dashboard: <I><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></I>,
    users: <I><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></I>,
    shield: <I><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></I>,
    box: <I><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></I>,
    building: <I><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4" /><line x1="8" y1="6" x2="8" y2="6" /><line x1="12" y1="6" x2="12" y2="6" /><line x1="16" y1="6" x2="16" y2="6" /><line x1="8" y1="10" x2="8" y2="10" /><line x1="12" y1="10" x2="12" y2="10" /><line x1="16" y1="10" x2="16" y2="10" /><line x1="8" y1="14" x2="8" y2="14" /><line x1="12" y1="14" x2="12" y2="14" /><line x1="16" y1="14" x2="16" y2="14" /></I>,
    gear: <I><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></I>,
    layers: <I><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></I>,
    ruler: <I><path d="M2 4h4v16H2z" /><line x1="6" y1="8" x2="4" y2="8" /><line x1="6" y1="12" x2="3" y2="12" /><line x1="6" y1="16" x2="4" y2="16" /><path d="M14 4h4v16h-4z" /><line x1="18" y1="8" x2="16" y2="8" /><line x1="18" y1="12" x2="15" y2="12" /><line x1="18" y1="16" x2="16" y2="16" /></I>,
    truck: <I><rect x="1" y="3" width="15" height="13" rx="2" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></I>,
    fileText: <I><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></I>,
    sliders: <I><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></I>,
    wrench: <I><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" /></I>,
    alertTriangle: <I><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></I>,
    tool: <I><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" /></I>,
    rotateCcw: <I><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 102.13-9.36L1 10" /></I>,
    clipboard: <I><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /></I>,
    messageCircle: <I><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" /></I>,
    landmark: <I><line x1="3" y1="22" x2="21" y2="22" /><line x1="6" y1="18" x2="6" y2="11" /><line x1="10" y1="18" x2="10" y2="11" /><line x1="14" y1="18" x2="14" y2="11" /><line x1="18" y1="18" x2="18" y2="11" /><polygon points="12 2 20 7 4 7" /></I>,
    activity: <I><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></I>,
    plug: <I><path d="M9 2v6" /><path d="M15 2v6" /><path d="M6 8h12v4a6 6 0 01-12 0V8z" /><line x1="12" y1="18" x2="12" y2="22" /></I>,
    arrowLeft: <I><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></I>,
    chevronDown: <I><polyline points="6 9 12 15 18 9" /></I>,
};

const NAV_GROUPS: NavGroup[] = [
    {
        key: 'users',
        labelKey: 'nav.admin.group.users',
        defaultLabel: 'Users',
        items: [
            { path: '/admin', labelKey: 'nav.admin.dashboard', defaultLabel: 'Dashboard', icon: Icons.dashboard },
            { path: '/admin/all-users', labelKey: 'nav.admin.allUsers', defaultLabel: 'All users', icon: Icons.users },
        ],
    },
    {
        key: 'productConfig',
        labelKey: 'nav.admin.group.productConfig',
        defaultLabel: 'Product configuration',
        items: [
            { path: '/admin/articles', labelKey: 'nav.admin.articles', defaultLabel: 'Articles', icon: Icons.box },
            { path: '/admin/departments', labelKey: 'nav.admin.departments', defaultLabel: 'Departments', icon: Icons.building },
            { path: '/admin/responsibilities', labelKey: 'nav.admin.processes', defaultLabel: 'Processes', icon: Icons.gear },
            { path: '/admin/materials', labelKey: 'nav.admin.materials', defaultLabel: 'Materials', icon: Icons.layers },
            { path: '/admin/units', labelKey: 'nav.admin.units', defaultLabel: 'Units', icon: Icons.ruler },
            { path: '/admin/suppliers', labelKey: 'nav.admin.suppliers', defaultLabel: 'Suppliers', icon: Icons.truck },
        ],
    },
    {
        key: 'moduleSettings',
        labelKey: 'nav.admin.group.moduleSettings',
        defaultLabel: 'Module settings',
        items: [
            { path: '/admin/offer-settings', labelKey: 'nav.admin.offerSettings', defaultLabel: 'Offer', icon: Icons.fileText },
            { path: '/admin/production-settings', labelKey: 'nav.admin.productionSettings', defaultLabel: 'Production', icon: Icons.sliders },
            { path: '/admin/equipments', labelKey: 'nav.admin.equipments', defaultLabel: 'Equipment', icon: Icons.wrench },
            { path: '/admin/installation-block-categories', labelKey: 'nav.admin.installationBlocks', defaultLabel: 'Installation blocks', icon: Icons.alertTriangle },
            { path: '/admin/servicing-types', labelKey: 'nav.admin.servicingTypes', defaultLabel: 'Service types', icon: Icons.tool },
            { path: '/admin/return-categories', labelKey: 'nav.admin.returnCategories', defaultLabel: 'Return categories', icon: Icons.rotateCcw },
            { path: '/admin/measurement-sketch-templates', labelKey: 'nav.admin.sketchTemplates', defaultLabel: 'Measurement sketches', icon: Icons.clipboard },
            { path: '/admin/impression-questions', labelKey: 'nav.admin.impressionQuestions', defaultLabel: 'Impression questions', icon: Icons.messageCircle },
        ],
    },
    {
        key: 'company',
        labelKey: 'nav.admin.group.company',
        defaultLabel: 'Company',
        items: [
            { path: '/admin/company-profile', labelKey: 'nav.admin.companyProfile', defaultLabel: 'Company profile', icon: Icons.landmark },
            { path: '/admin/audit-log', labelKey: 'nav.admin.auditLog', defaultLabel: 'Audit log', icon: Icons.activity },
        ],
    },
    {
        key: 'integrations',
        labelKey: 'nav.admin.group.integrations',
        defaultLabel: 'Integrations',
        items: [
            { path: '/admin/integrations/kubit', labelKey: 'integrations.kubit.title', defaultLabel: 'KuBIT Integration', icon: Icons.plug },
        ],
    },
];

const loadOpenState = (): Record<string, boolean> => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) return JSON.parse(stored);
    } catch {}
    const defaults: Record<string, boolean> = {};
    NAV_GROUPS.forEach(g => { defaults[g.key] = true; });
    return defaults;
};

const AdminNav: React.FC<AdminNavProps> = ({ currentPath, onLinkClick, collapsed = false }) => {
    const intl = useIntl();
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(loadOpenState);

    // Per-tenant flag controlled by Mika Ops. When false, every link in
    // the "integrations" nav group is filtered out (currently just the
    // KuBIT link). The route is also blocked at the admin shell level so
    // direct URL navigation gets redirected.
    const kubitEnabled = useSelector((state: RootState) => Boolean((state.app?.companyProfile as any)?.kubitEnabled));

    const visibleGroups = NAV_GROUPS.map(group => {
        if (group.key !== 'integrations') return group;
        const filteredItems = group.items.filter(item => {
            if (item.path === '/admin/integrations/kubit') return kubitEnabled;
            return true;
        });
        return { ...group, items: filteredItems };
    }).filter(group => group.items.length > 0);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(openGroups));
    }, [openGroups]);

    const toggleGroup = (key: string) => {
        setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const isActive = (path: string) => {
        if (path === '/admin') return currentPath === '/admin' || currentPath === '/admin/';
        return currentPath.startsWith(path);
    };

    return (
        <nav className="admin-nav-root">
            <div className="admin-nav-scroll">
                {visibleGroups.map(group => {
                    // In the rail every group's items show (as icons), ignoring the
                    // per-group open state — same as NavbarV2's system rail.
                    const itemsShown = collapsed || openGroups[group.key];
                    return (
                        <div className="an-group" key={group.key}>
                            <button
                                className="an-group-header"
                                onClick={() => toggleGroup(group.key)}
                                type="button"
                                aria-expanded={!!openGroups[group.key]}
                                tabIndex={collapsed ? -1 : 0}
                            >
                                <span className="an-group-label">
                                    <FormattedMessage id={group.labelKey} defaultMessage={group.defaultLabel} />
                                </span>
                                <span className={`an-group-chevron ${openGroups[group.key] ? 'open' : ''}`}>
                                    {Icons.chevronDown}
                                </span>
                            </button>
                            {itemsShown && (
                                <div className="an-group-items">
                                    {group.items.map(item => {
                                        const label = intl.formatMessage({ id: item.labelKey, defaultMessage: item.defaultLabel });
                                        return (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                className={`an-item ${isActive(item.path) ? 'active' : ''}`}
                                                onClick={onLinkClick}
                                                data-tooltip={label}
                                            >
                                                <span className="an-item-icon">{item.icon}</span>
                                                <span className="an-item-label">
                                                    <FormattedMessage id={item.labelKey} defaultMessage={item.defaultLabel} />
                                                </span>
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* "Back to system" intentionally removed — NavbarV2's footer
                already renders a "Kthehu te sistemi" button when in /admin/*,
                so showing it again here was a duplicate. */}
        </nav>
    );
};

export default AdminNav;
